import { type ReactNode, useCallback } from 'react'
import { ButtonAdd, Page } from '@components'
import { useAddAccountStatus, useTranslation } from '@front/model'
import { useToastError } from '@hooks'
import { isNotEmpty, useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { useNavigate, createFileRoute } from '@tanstack/react-router'
import { DEFAULT_REQUIRED_RESIDENT_FIELDS, type UnsavedAccountStatus } from '@terros/common'
import { WorkflowDeprecatedCheck } from 'app/components/workflow'
import { AccountStatusForm } from './form'
import type { AccountStatusFormData } from './types'
import { fromAccountStatusFormData } from './util'

function AddStatusPage(): ReactNode {
  return (
    <WorkflowDeprecatedCheck deprecateIfWorkflowEnabled>
      <AddStatus />
    </WorkflowDeprecatedCheck>
  )
}

const AddStatus = (): ReactNode => {
  const navigate = useNavigate()
  const { add, isAdding, error } = useAddAccountStatus()
  const { t } = useTranslation()
  useToastError(error)
  const form = useForm<AccountStatusFormData<UnsavedAccountStatus>>({
    initialValues: {
      name: '',
      order: 0,
      short: '',
      plural: '',
      description: '',
      integrationMapping: [],
      scope: 'self',
      setter: {},
      closer: {},
      selfgen: {},
      ioType: 'None',
      requiredFields: DEFAULT_REQUIRED_RESIDENT_FIELDS,
    },
    validate: {
      name: isNotEmpty(t('Name is required')),
      order: isNotEmpty(t('Order is required')),
    },
    validateInputOnChange: true,
  })
  const handleSubmit = useCallback(
    async (unsaved: AccountStatusFormData<UnsavedAccountStatus>): Promise<void> => {
      await add(fromAccountStatusFormData(unsaved))
      navigate({ to: '..' })
      form.resetDirty(unsaved)
      notifications.show({ message: t('Stage added'), color: 'green' })
    },
    [add, navigate, form, t]
  )

  return (
    <form className='w-full' onSubmit={form.onSubmit(handleSubmit)}>
      <Page>
        <Page.Heading back title={t('Add Stage')}>
          <ButtonAdd disabled={!form.isDirty()} loading={isAdding} type='submit' />
        </Page.Heading>
        <Page.Content scrollArea>
          <AccountStatusForm form={form} />
        </Page.Content>
      </Page>
    </form>
  )
}

export const Route = createFileRoute('/settings/status_/add/')({
  component: AddStatusPage,
})
